import React, { useCallback } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { Button, ButtonType } from "components/common/Button.component";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { ConfirmActionProps } from "types/modals";

const ConfirmAction = ({
    more,
    closeModal,
    loopFocus,
    initialFocusRef,
}: ConfirmActionProps): React.ReactElement => {
    const { translate } = useTranslation();

    const {
        message,
        moreContent,
        children,
        confirmButtonText = "popUp.ok",
        cancelButtonText = "popUp.cancel",
        confirmButtonColor,
        hideClose = false,
        enableSaveDiscard = false,
        isHandleMultipleActions = false,
        handleAction,
        saveCallback,
        discardCallback,
    } = more ?? {};

    const confirmType =
        confirmButtonColor === "btn-default" ? ButtonType.SECONDARY : ButtonType.PRIMARY;

    const onConfirm = useCallback(() => {
        handleAction?.();
    }, [handleAction]);

    const onSave = useCallback(() => {
        saveCallback?.();
        closeModal();
    }, [saveCallback, closeModal]);

    const onDiscard = useCallback(() => {
        discardCallback?.();
        closeModal();
    }, [discardCallback, closeModal]);

    return (
        <>
            <div className="ga-modal__content">
                {children}
                {moreContent}
                {message && <p className="paragraph-space">{message}</p>}
            </div>
            <div className="ga-modal__actions">
                {enableSaveDiscard ? (
                    <>
                        <Button
                            type={ButtonType.SECONDARY}
                            onClick={onDiscard}
                            ref={initialFocusRef}
                            tabIndex={MODAL_CONTENT_TAB_INDEX}
                            text={translate("popUp.confirm.discard")}
                        />
                        <Button
                            type={ButtonType.SECONDARY}
                            onClick={onSave}
                            onBlur={loopFocus}
                            tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                            text={
                                isHandleMultipleActions
                                    ? translate("popUp.confirm.saveAll")
                                    : translate("popUp.confirm.save")
                            }
                        />
                    </>
                ) : (
                    <>
                        <Button
                            type={confirmType}
                            onClick={onConfirm}
                            ref={initialFocusRef}
                            tabIndex={MODAL_CONTENT_TAB_INDEX}
                            text={translate(confirmButtonText)}
                        />
                        {!hideClose && (
                            <Button
                                type={ButtonType.SECONDARY}
                                onClick={closeModal}
                                onBlur={loopFocus}
                                tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                                text={translate(cancelButtonText)}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default ConfirmAction;
